import React, { useEffect, useMemo, lazy, Suspense } from 'react';
import { observer } from 'mobx-react';

import {
    Tooltip,
    Button,
    Card,
    Checkbox,
    Col,
    Divider,
    Form,
    Input,
    InputNumber,
    message,
    notification,
    Row,
    Spin,
} from 'antd';

import SalesOrderStore from '../../stores/SalesOrderStore';
import {
    AdditionalRequirements,
    ErrorRender,
    FormErrorParser,
    Price,
    ShippingCarriers,
    ShippingMethods,
    SquarePaymentForm,
    UploadFile,
    ButtonLinkPreviewFAI,
} from '@partsbadger/library';
import ContactStore from '../../stores/ContactStore';
import { PartPreview } from '../Quotes';

import { allowed_2d, allowed_3d, SQUARE_APP_ID, SQUARE_ENVIRONMENT_URL, SQUARE_LOCATION_ID } from '@partsbadger/utils';
import { ContactProps, OrderLineItemProps, SalesOrderProps } from '../../types/types';
import { ICustomRequirements } from '@partsbadger/types';
import { EditFilled, PlusCircleFilled } from '@ant-design/icons';

import Addresses from './Addresses';

import dayjs from 'dayjs';

interface SalesOrderToolProps {
    contact: ContactProps;
    order: SalesOrderProps;
    order_items: OrderLineItemProps[];
    onSubmitted: (data: { showQuoteSurvey?: boolean }) => void;
}

const SalesOrderCreateForm = observer((props: SalesOrderToolProps) => {
    const [validateStatus, setValidateStatus] = React.useState<'success' | 'error'>('success');

    const [form] = Form.useForm();
    const { getFieldValue, setFieldsValue } = form;

    const [isSendingOrder, setIsSendingOrder] = React.useState<boolean>(false);

    const { order_items, order, contact } = props;

    const isAutoGreen = order_items.every(
        item => item.quote_has_product?.safe_auto_quote === true && item.quote_has_product.quote_action === 'buy_now'
    );

    const isAnyOriginalQuoteCompleted =
        order_items?.some(item => item?.quote_has_product?.quote_related?.duplicated_from?.completed) ?? false;

    const buttonReference = React.useRef<HTMLButtonElement>(null);

    const sendOrder = async (values: any) => {
        setIsSendingOrder(true);

        const payload = {
            shipping_account_number: values.shipping_account_number,
            shipping_method: values.shipping_method,
            carrier: values.carrier,

            additional_files: values.additional_files?.map((f: any) => f.response.id),
            po_files: values.po_files?.map((po: any) => {
                return po.response.id;
            }),

            notes: values.notes,
            create_2d_files: values.create_2d_files,
            create_3d_files: values.create_3d_files,
            source: 'Sales Order Tool - Customer',
            pay_with_credit_card: values.pay_with_credit_card,
            credit_card_token: values.credit_card_token,
            phone: values.phone,
            purchase_order: values.purchase_order,

            additional_requirements: values.additional_requirements?.map((r: any) => {
                return r.value ? `${r.label} : ${r.value}` : r.label;
            }),
        };

        const isDraftQuote = SalesOrderStore.order_items[0]?.quote_has_product?.quote_related?.stage === 'Draft';

        await SalesOrderStore.sendToReview(order.id, payload)
            .then(async () => {
                // Display the Quote survey only if the order was created via the "Complete and Checkout" option in Get Quote.
                props.onSubmitted({ showQuoteSurvey: isDraftQuote ? true : false });
                cleanStore();
                if (values.pay_with_credit_card) {
                    const payload = {
                        amount: String(order.grand_total),
                        customer_email: contact?.email,
                        representative_email: contact?.owner?.email,
                        notes: values.notes,
                        quotes: SalesOrderStore.quotes,
                        ship_cost: '0',
                        tax_cost: '0',
                        zip_code: String(order.billing_zip_code ?? ''),
                        // files: values?.files ? values?.files?.fileList?.map(f => f.originFileObj) : [],
                    };
                    await SalesOrderStore.sendCreditCard(payload);
                }
            })
            .catch((err: any) => {
                notification.error({
                    duration: 10,
                    message: 'Error',
                    description: <ErrorRender error={err} />,
                    placement: 'bottomRight',
                });
            })
            .finally(() => setIsSendingOrder(false));
    };

    const cleanStore = () => {
        // Cleaning selected quotes
        setFieldsValue({
            quotes: [],
            shipping_address: null,
            billing_address: null,
        });
    };

    const truncateFileNameString = (text: string | null, length: number) => {
        if (text) {
            const fileName = text.split('.')?.pop();
            return text && text?.length > length ? text.substring(0, length).concat('...') + fileName : text;
        }
    };

    /**
     * Initialize Store
     */
    useEffect(() => {
        cleanStore();
    }, []);

    const create_2d_files = order.create_2d_files;
    const create_3d_files = order.create_3d_files;

    // Check if there are pending files or the customer decide to partsbadger create production files
    const areThere2DFilesPending = isAutoGreen ? false : SalesOrderStore.areThere2DFilesPending;
    const areThere3DFilesPending = SalesOrderStore.areThere3DFilesPending;

    const doWeNeedAskForFiles =
        (areThere2DFilesPending && !create_2d_files) || (areThere3DFilesPending && !create_3d_files);

    const addressValidator = async (type: string) => {
        switch (type) {
            case 'Billing':
                if (!order?.billing_street) {
                    return true;
                } else {
                    form.setFields([{ name: 'billing_address', value: order?.billing_street, errors: [] }]);

                    return false;
                }
            case 'Shipping':
                if (!order?.shipping_street) {
                    return true;
                } else {
                    form.setFields([{ name: 'shipping_address', value: order?.shipping_street, errors: [] }]);

                    return false;
                }
            default:
                return null;
        }
    };

    const isNoSafeAutoQuoteAndBuyNow = useMemo(() => {
        return order.line_items.some(
            item =>
                item.quote_has_product?.safe_auto_quote === false && item.quote_has_product?.quote_action === 'buy_now'
        );
    }, [order.line_items]);

    const [preSelectedCustomRequirements, setPreSelectedCustomRequirements] = React.useState<ICustomRequirements[]>([]);
    const catchCustomRequirements = (custom_requirements: ICustomRequirements[]) => {
        if (!Array.isArray(custom_requirements)) return [];

        const formattedRequirements = custom_requirements.map(req => ({
            key: req.key,
            label: req.label,
            value: req.value,
        }));

        setPreSelectedCustomRequirements(formattedRequirements);
    };

    return (
        <>
            <Form
                className={'sales-order-form'}
                layout="vertical"
                form={form}
                scrollToFirstError={true}
                onFinish={values => {
                    if (!values.pay_with_credit_card && !values.po_files) {
                        message.error('Please upload Purchase Order Files');
                    } else {
                        if (values.pay_with_credit_card) {
                            if (buttonReference.current) {
                                buttonReference.current.click();
                            }
                        } else {
                            sendOrder(values);
                        }
                    }
                }}
                onFinishFailed={errorInfo => {
                    if (buttonReference.current) {
                        buttonReference.current.click();
                    }

                    notification.error({
                        key: 'form-errors',
                        duration: 8,
                        message: 'Please input all required fields',
                        description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                        placement: 'top',
                        maxCount: 1,
                    });

                    // form.scrollToField('billing_address');
                }}
            >
                <Row gutter={[16, 8]}>
                    <Col md={12} span={24}>
                        <Card>
                            <div className="addresses">
                                <div className="billing-address">
                                    <Form.Item dependencies={['billing_address']} noStyle>
                                        {() => {
                                            addressValidator('Billing');

                                            return (
                                                <Form.Item
                                                    name="billing_address"
                                                    label={
                                                        <span className="font-weight-bold" style={{ color: '#455560' }}>
                                                            Billing Address
                                                        </span>
                                                    }
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Billing Address is required',
                                                        },
                                                    ]}
                                                >
                                                    <Addresses order={order} type={'Billing'} />
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>
                                </div>

                                <div className="shipping-address">
                                    <Form.Item dependencies={['shipping_address']} noStyle>
                                        {() => {
                                            addressValidator('Shipping');

                                            return (
                                                <Form.Item
                                                    name="shipping_address"
                                                    label={
                                                        <span className="font-weight-bold" style={{ color: '#455560' }}>
                                                            Shipping Address
                                                        </span>
                                                    }
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message: 'Shipping Address is required',
                                                        },
                                                    ]}
                                                >
                                                    <Addresses order={order} type={'Shipping'} />
                                                </Form.Item>
                                            );
                                        }}
                                    </Form.Item>
                                </div>
                            </div>
                            <Row gutter={[16, 8]} className="shipping-information d-flex align-items-end">
                                <Col span={12}>
                                    <Form.Item
                                        label={'Shipping Account (Optional)'}
                                        name="shipping_account_number"
                                        initialValue={contact.account?.shipping_account_number}
                                    >
                                        <Input placeholder="Shipping Account Number" />
                                    </Form.Item>
                                </Col>

                                <Col span={12}>
                                    <Form.Item label={'Carrier'} name="carrier" initialValue={contact.account?.carrier}>
                                        <ShippingCarriers size="large" />
                                    </Form.Item>
                                </Col>

                                <Col span={12}>
                                    <Form.Item
                                        label={'Shipping Method'}
                                        name="shipping_method"
                                        initialValue={contact.account?.shipping_method}
                                    >
                                        <ShippingMethods size="large" />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        label="Phone number"
                                        name="phone"
                                        initialValue={contact?.phone ?? ''}
                                        rules={[
                                            {
                                                required: true,
                                                message: 'Please input your phone number',
                                            },
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <div className="additional-information">
                                <p
                                    className="font-weight-bold my-4"
                                    style={{
                                        fontSize: 18,
                                    }}
                                >
                                    Files and Additional Information
                                </p>

                                <div className={'flex flex-col items-center'}>
                                    {!contact?.is_hobbyist && (
                                        <>
                                            <div className={'order-requirements'}>
                                                <Form.Item
                                                    label={'Order Requirements'}
                                                    style={{
                                                        marginBottom: 1,
                                                    }}
                                                    help={
                                                        'Some additional requirements may add additional cost. These costs will be ' +
                                                        'confirmed with you prior to production'
                                                    }
                                                    name="additional_requirements"
                                                    initialValue={ContactStore.default_requirements?.map((r, index) => {
                                                        return {
                                                            key: index + 100000,
                                                            label: r.name,
                                                        };
                                                    })}
                                                >
                                                    <AdditionalRequirements
                                                        onChange={values =>
                                                            catchCustomRequirements(values as ICustomRequirements[])
                                                        }
                                                        requirements={ContactStore.requirements_list}
                                                    />
                                                    <ButtonLinkPreviewFAI
                                                        custom_requirements={preSelectedCustomRequirements}
                                                    />
                                                </Form.Item>
                                            </div>

                                            <div className={'w-full'}>
                                                <Form.Item
                                                    label="Purchase Order Number"
                                                    name="purchase_order"
                                                    rules={[
                                                        {
                                                            required: false,
                                                            message: 'Purchase Order Number is required',
                                                        },
                                                    ]}
                                                    initialValue={order?.purchase_order}
                                                >
                                                    <Input />
                                                </Form.Item>
                                            </div>

                                            <div className={'w-full mx-4'}>
                                                <Form.Item
                                                    dependencies={['pay_with_credit_card']} // if using dependencies
                                                    noStyle
                                                >
                                                    {({ getFieldValue }) => {
                                                        // getFieldValue defined here
                                                        return (
                                                            <Form.Item
                                                                label="Purchase Order Files"
                                                                style={{
                                                                    marginBottom: 1,
                                                                }}
                                                                name="po_files"
                                                                valuePropName="fileList"
                                                                rules={[
                                                                    {
                                                                        required: !getFieldValue(
                                                                            'pay_with_credit_card'
                                                                        ),
                                                                        message: 'Please upload a purchase order file',
                                                                    },
                                                                ]}
                                                            >
                                                                <UploadFile multiple />
                                                            </Form.Item>
                                                        );
                                                    }}
                                                </Form.Item>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className={'production-part-files mt-4'}>
                                    {/*<pre>{JSON.stringify(areThere3DFilesPending, null, 2)}</pre>*/}
                                    <Form.Item
                                        label="Production Files"
                                        help={
                                            <div>
                                                {!contact?.is_hobbyist && (
                                                    <>
                                                        <p>
                                                            Part files are required for production. We have a team of
                                                            Design Engineers that can create your files for an
                                                            additional cost, we will supply the files for approval prior
                                                            to production.
                                                        </p>
                                                        <p className="mt-1">
                                                            <strong>Acceptable 3D File Types:</strong>{' '}
                                                            {allowed_3d.replace(/,/g, ' ')}
                                                        </p>
                                                        {!isAutoGreen && (
                                                            <p>
                                                                <strong>Acceptable 2D File Types:</strong>{' '}
                                                                {allowed_2d.replace(/,/g, ' ')}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        }
                                        name="production_files"
                                        rules={[
                                            {
                                                required: doWeNeedAskForFiles,
                                                message:
                                                    'Please upload the pending file(s) for production or request for us to create them for you',
                                            },
                                        ]}
                                    >
                                        <Card>
                                            {order_items.map(
                                                (item, index) =>
                                                    item.quote_has_product && (
                                                        <div className="flex flex-row justify-center" key={item?.id}>
                                                            <div className="w-full lg:w-1/3">
                                                                {index === 0 && <p>Part Name</p>}
                                                                <p
                                                                    className="flex items-center mr-1"
                                                                    style={{ height: '40px' }}
                                                                >
                                                                    {item.name}
                                                                </p>
                                                            </div>
                                                            <div className="w-full lg:w-1/3">
                                                                {index === 0 && <p>2D files</p>}
                                                                {item.production_2d_file ? (
                                                                    <div
                                                                        className="flex items-center mr-1"
                                                                        title={item.production_2d_file.filename ?? ' '}
                                                                        style={{ height: '40px' }}
                                                                    >
                                                                        <span className="break-words truncate w-10 sm:w-24">
                                                                            {truncateFileNameString(
                                                                                item.production_2d_file.filename,
                                                                                8
                                                                            )}
                                                                        </span>
                                                                        {isAutoGreen || contact?.is_hobbyist ? (
                                                                            <Tooltip title="File uploads are disabled for this user">
                                                                                <EditFilled
                                                                                    style={{
                                                                                        color: '#ccc',
                                                                                        cursor: 'not-allowed',
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <UploadFile
                                                                                className={'ml-2'}
                                                                                fileType="2D"
                                                                                renderAs="button"
                                                                                showUploadList={false}
                                                                                onUpload={f => {
                                                                                    SalesOrderStore.updateOrderItemDraft(
                                                                                        order.id,
                                                                                        item.id,
                                                                                        {
                                                                                            quantity: item.quantity,
                                                                                            production_2d_file:
                                                                                                f.response.id,
                                                                                        }
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <EditFilled
                                                                                    title={'Replace File'}
                                                                                    className="cursor-pointer"
                                                                                />
                                                                            </UploadFile>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="flex items-center mr-1"
                                                                        style={{ height: '40px' }}
                                                                    >
                                                                        <Form.Item
                                                                            name="2dFile"
                                                                            rules={[
                                                                                {
                                                                                    required: false,
                                                                                    message: '2D file is required',
                                                                                },
                                                                            ]}
                                                                            style={{
                                                                                margin: 0,
                                                                            }}
                                                                        >
                                                                            <span
                                                                                style={{
                                                                                    color: !isAutoGreen
                                                                                        ? 'red'
                                                                                        : 'gray',
                                                                                    opacity: isAutoGreen ? 0.5 : 1,
                                                                                }}
                                                                            >
                                                                                Pending 2D File
                                                                            </span>
                                                                            {!isAutoGreen && (
                                                                                <UploadFile
                                                                                    className={'ml-2'}
                                                                                    fileType="2D"
                                                                                    renderAs="button"
                                                                                    showUploadList={false}
                                                                                    onUpload={f => {
                                                                                        if (isAutoGreen) return;
                                                                                        SalesOrderStore.updateOrderItemDraft(
                                                                                            order.id,
                                                                                            item.id,
                                                                                            {
                                                                                                quantity: item.quantity,
                                                                                                production_2d_file:
                                                                                                    f.response.id,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <PlusCircleFilled
                                                                                        title={'Add a File'}
                                                                                        className="cursor-pointer"
                                                                                    />
                                                                                </UploadFile>
                                                                            )}
                                                                        </Form.Item>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="w-full lg:w-1/3">
                                                                {index === 0 && <p>3D files</p>}
                                                                {item.production_3d_file ? (
                                                                    <div
                                                                        className="flex items-center break-words w-auto md:w-4/5"
                                                                        title={item.production_3d_file.filename ?? ' '}
                                                                        style={{ height: '40px' }}
                                                                    >
                                                                        <span className="break-words truncate w-10 sm:w-24">
                                                                            {truncateFileNameString(
                                                                                item.production_3d_file.filename,
                                                                                8
                                                                            )}
                                                                        </span>
                                                                        {isAutoGreen || contact?.is_hobbyist ? (
                                                                            <Tooltip title="File uploads are disabled for this user">
                                                                                <EditFilled
                                                                                    style={{
                                                                                        color: '#ccc',
                                                                                        cursor: 'not-allowed',
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        ) : (
                                                                            <UploadFile
                                                                                className={'ml-2'}
                                                                                fileType="3D"
                                                                                renderAs="button"
                                                                                showUploadList={false}
                                                                                onUpload={f => {
                                                                                    SalesOrderStore.updateOrderItemDraft(
                                                                                        order.id,
                                                                                        item.id,
                                                                                        {
                                                                                            quantity: item.quantity,
                                                                                            production_3d_file:
                                                                                                f.response.id,
                                                                                        }
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <EditFilled
                                                                                    title={'Replace File'}
                                                                                    className="cursor-pointer"
                                                                                />
                                                                            </UploadFile>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <Form.Item
                                                                        name="3dFile"
                                                                        rules={[
                                                                            {
                                                                                required: false,
                                                                                message: '3D file is required',
                                                                            },
                                                                        ]}
                                                                        style={{
                                                                            margin: 0,
                                                                        }}
                                                                    >
                                                                        <span style={{ color: 'red' }}>
                                                                            Pending 3D File
                                                                        </span>
                                                                        <UploadFile
                                                                            className={'ml-2'}
                                                                            fileType="3D"
                                                                            renderAs="button"
                                                                            showUploadList={false}
                                                                            onUpload={f => {
                                                                                SalesOrderStore.updateOrderItemDraft(
                                                                                    order.id,
                                                                                    item.id,
                                                                                    {
                                                                                        quantity: item.quantity,
                                                                                        production_3d_file:
                                                                                            f.response.id,
                                                                                    }
                                                                                );
                                                                            }}
                                                                        >
                                                                            <PlusCircleFilled
                                                                                title={'Add a File'}
                                                                                className="cursor-pointer"
                                                                            />
                                                                        </UploadFile>
                                                                    </Form.Item>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )
                                            )}
                                        </Card>
                                    </Form.Item>
                                    {(isAutoGreen ||
                                        !contact?.is_hobbyist ||
                                        (contact?.is_hobbyist && isAnyOriginalQuoteCompleted)) && (
                                        <div className={'d-flex flex-row mt-2 items-center'}>
                                            {areThere3DFilesPending && (
                                                <div className={'w-full pr-2'}>
                                                    <Form.Item
                                                        label=""
                                                        name="create_3d_files"
                                                        valuePropName="checked"
                                                        initialValue={order.create_3d_files}
                                                    >
                                                        <Checkbox
                                                            key={'create_3d_files'}
                                                            className={'italic'}
                                                            onChange={e => {
                                                                SalesOrderStore.update_sales_order_field(
                                                                    'create_3d_files',
                                                                    e.target.checked
                                                                );
                                                            }}
                                                        >
                                                            For production, would you like us to create a 3D file for
                                                            you?
                                                        </Checkbox>
                                                    </Form.Item>
                                                </div>
                                            )}

                                            {areThere2DFilesPending && (
                                                <div className={'w-full pr-2'}>
                                                    <Form.Item
                                                        label=""
                                                        name="create_2d_files"
                                                        valuePropName="checked"
                                                        initialValue={order.create_2d_files}
                                                    >
                                                        <Checkbox
                                                            key={'create_2d_files'}
                                                            className={'italic'}
                                                            onChange={e => {
                                                                SalesOrderStore.update_sales_order_field(
                                                                    'create_2d_files',
                                                                    e.target.checked
                                                                );
                                                            }}
                                                        >
                                                            For production, would you like us to create a 2D file for
                                                            you?
                                                        </Checkbox>
                                                    </Form.Item>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {!contact?.is_hobbyist && (
                                    <div className={'additional-files mt-4'}>
                                        <Form.Item
                                            label={'Additional Files'}
                                            style={{
                                                marginBottom: 1,
                                            }}
                                            name="additional_files"
                                            valuePropName="fileList"
                                        >
                                            <UploadFile multiple />
                                        </Form.Item>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </Col>

                    <Col md={12} span={24}>
                        <Card>
                            <div className="review-items">
                                <div className="font-weight-bold font-size-lg">Order Review</div>
                                <Spin spinning={SalesOrderStore.loading}>
                                    <Row className={'row-order'}>
                                        {order_items.map(item => {
                                            return (
                                                <React.Fragment key={item.id}>
                                                    <div key={item.id} className="d-flex flex-column justify-end w-100">
                                                        <div className="d-flex flex-column">
                                                            <div
                                                                className="d-flex flex-row  align-items-center w-100"
                                                                style={{ minHeight: '50px' }}
                                                            >
                                                                {item.quote_has_product && (
                                                                    <div className="d-none d-sm-block mr-4">
                                                                        <PartPreview
                                                                            thumbnail={item.quote_has_product.thumbnail}
                                                                            has_file_3d={
                                                                                item.quote_has_product.has_file_3d
                                                                            }
                                                                            width="75px"
                                                                        />
                                                                    </div>
                                                                )}

                                                                <div className="font-size-base font-weight-bold mb-1">
                                                                    {item.display_name}
                                                                </div>

                                                                <div className="d-flex align-items-start justify-content-end ml-auto">
                                                                    <Price
                                                                        value={item.total_price}
                                                                        style={{
                                                                            fontSize: 16,
                                                                            fontWeight: 400,
                                                                        }}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-row items-end justify-content-between">
                                                            <div style={{ width: '220px' }}>
                                                                {!item.quote_has_product ? (
                                                                    <>
                                                                        <div className="font-thin">
                                                                            {/*{item.product_description}*/}
                                                                        </div>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {/* <div className="font-weight-bold mb-1">{item.name}</div> */}
                                                                        {/* <PartDimensions
                                                                        {...item.quote_has_product?.product
                                                                            .dimensions_dict}
                                                                    /> */}

                                                                        <div className="d-flex flex-column">
                                                                            <div className="d-flex flex-row mr-2">
                                                                                <div className="font-weight-bold">
                                                                                    Material:
                                                                                </div>
                                                                                <div className="font-thin ml-1">
                                                                                    {
                                                                                        item.quote_has_product
                                                                                            ?.material_related.name
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex flex-row mr-2">
                                                                                <div className="font-weight-bold">
                                                                                    Finish:
                                                                                </div>
                                                                                <div className="font-thin ml-1">
                                                                                    {
                                                                                        item.quote_has_product
                                                                                            ?.finish_related?.name
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                            <div className="d-flex flex-row mr-2">
                                                                                <div className="font-weight-bold">
                                                                                    Coating:
                                                                                </div>
                                                                                <div className="font-thin ml-1">
                                                                                    {
                                                                                        item.quote_has_product
                                                                                            ?.coating_related?.name
                                                                                    }
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {!isNoSafeAutoQuoteAndBuyNow && (
                                                                    <div className="actions">
                                                                        <Button
                                                                            type="link"
                                                                            className="p-0 m-0"
                                                                            onClick={() => {
                                                                                SalesOrderStore.removeOrderItemDraft(
                                                                                    order.id,
                                                                                    item.id
                                                                                );
                                                                            }}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="d-flex flex-column-reverse justify-content-end flex-lg-row">
                                                                <div className="d-flex flex-column align-items-start justify-content-start">
                                                                    <span className="font-weight-bold mr-2 mb-1">
                                                                        Quantity:
                                                                    </span>

                                                                    <Form.Item
                                                                        name={['order_items', item.id, 'quantity']}
                                                                        label="Quantity"
                                                                        className="d-flex flex-row-row-reverse"
                                                                        validateStatus={validateStatus}
                                                                        rules={[
                                                                            {
                                                                                required: true,
                                                                                message: 'Quantity is required!',
                                                                            },
                                                                        ]}
                                                                        help={
                                                                            validateStatus === 'error' &&
                                                                            `Select a number between 1 and ${
                                                                                item.quote_has_product
                                                                                    ?.has_dynamic_quantities
                                                                                    ? item.quote_has_product
                                                                                          ?.quantity_max
                                                                                    : 400
                                                                            }`
                                                                        }
                                                                        initialValue={item.quantity}
                                                                        noStyle
                                                                    >
                                                                        <div className={'text-center w-full'}>
                                                                            {item.quantity}
                                                                        </div>
                                                                    </Form.Item>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center text-gray-500">
                                                        {item.quote_has_product?.is_fastlane_safe &&
                                                            item.quote_has_product.material_disclaimer_message}
                                                    </div>

                                                    <Divider className="mt-1 mb-1" />
                                                </React.Fragment>
                                            );
                                        })}
                                    </Row>
                                </Spin>
                            </div>
                            <div className="billing-summary">
                                <div
                                    className="font-weight-bold my-4"
                                    style={{
                                        fontSize: 18,
                                    }}
                                >
                                    Order Summary
                                </div>
                                <div className={'d-flex flex-col justify-between'}>
                                    <div className="d-flex flex-row justify-between  mb-1">
                                        <div className="summary-label">Subtotal</div>
                                        <div className="summary-value">
                                            <Price value={order?.subtotal ?? 0} />
                                        </div>
                                    </div>

                                    <div className="d-flex flex-row justify-between mt-1">
                                        <div className="summary-label">Discount</div>
                                        <div className="summary-value">
                                            <Price value={order?.discount ?? 0} />
                                        </div>
                                    </div>

                                    <div className="d-flex flex-row justify-between  mt-2">
                                        <div className="summary-label">Tax</div>
                                        <div className="summary-value">
                                            <Price value={order?.taxes ?? 0} />
                                        </div>
                                    </div>

                                    <div
                                        className="d-flex flex-row justify-between  mt-2 pt-4"
                                        style={{
                                            borderTop: 'solid 1px #d9d9d9',
                                        }}
                                    >
                                        <div className="font-weight-bold text-xl">Grand Total</div>
                                        <div className="font-weight-bold text-xl">
                                            <Price value={order?.grand_total ?? 0} />
                                        </div>
                                    </div>
                                </div>

                                <p
                                    className="pt-4 font-bold"
                                    style={{
                                        textAlign: 'left', // optional for text alignment
                                    }}
                                >
                                    *Your order will include a Material Certificate and Certificate of Conformance.
                                </p>

                                {!contact?.is_hobbyist && (
                                    <div className="order-notes mt-4">
                                        <Form.Item label="Order Comments" name="notes">
                                            <Input.TextArea placeholder={'Type here...'} />
                                        </Form.Item>
                                    </div>
                                )}

                                <div className="additional-information">
                                    <div
                                        className="font-weight-bold my-4"
                                        style={{
                                            fontSize: 18,
                                        }}
                                    >
                                        Payment Method
                                    </div>
                                    <div className="square-payment">
                                        <div className={'payment d-flex flex-row items-center'}>
                                            <div className={'w-full'}>
                                                <Form.Item
                                                    label=""
                                                    className="mx-0 md:mx-2"
                                                    name="pay_with_credit_card"
                                                    valuePropName="checked"
                                                    rules={[
                                                        {
                                                            required: false,
                                                            message: 'Field is required',
                                                        },
                                                    ]}
                                                    initialValue={true}
                                                >
                                                    <Checkbox disabled={contact?.is_hobbyist ? true : false}>
                                                        Pay With Credit Card
                                                    </Checkbox>
                                                </Form.Item>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <Form.Item
                                    dependencies={['pay_with_credit_card']} // if using dependencies
                                    noStyle
                                >
                                    {({ getFieldValue }) => {
                                        // getFieldValue defined here
                                        return getFieldValue('pay_with_credit_card') ? ( // reference getFieldValue here
                                            <div className="text-left mt-2">
                                                <div>
                                                    <SquarePaymentForm
                                                        buttonRef={buttonReference}
                                                        app_id={SQUARE_APP_ID}
                                                        location_id={SQUARE_LOCATION_ID}
                                                        script_url={SQUARE_ENVIRONMENT_URL}
                                                        onPayment={sourceId => {
                                                            if (sourceId) {
                                                                form.validateFields()
                                                                    .then(async values => {
                                                                        const payload = {
                                                                            ...values,
                                                                            credit_card_token: sourceId,
                                                                        };

                                                                        sendOrder(payload);
                                                                    })
                                                                    .catch(errorInfo => {
                                                                        setIsSendingOrder(false);
                                                                    });
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        ) : null;
                                    }}
                                </Form.Item>

                                {contact?.is_hobbyist && (
                                    <Form.Item
                                        name="accepted_terms_conditions"
                                        rules={[
                                            {
                                                validator: (_, value) =>
                                                    value
                                                        ? Promise.resolve()
                                                        : Promise.reject(
                                                              new Error('Please accept the terms and conditions')
                                                          ),
                                            },
                                        ]}
                                        valuePropName="checked"
                                        initialValue={false}
                                        style={{
                                            marginBottom: 0,
                                        }}
                                    >
                                        <Checkbox>
                                            I have read and accept the{' '}
                                            <a
                                                href={'https://parts-badger.com/terms-and-conditions'}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                className="font-weight-bold underline"
                                            >
                                                terms and conditions.
                                            </a>
                                        </Checkbox>
                                    </Form.Item>
                                )}

                                <div className="terms-and-conditions">
                                    <Form.Item
                                        style={{
                                            marginBottom: 16,
                                        }}
                                        valuePropName="checked"
                                        name="terms-and-conditions"
                                        rules={[
                                            {
                                                validator: (_, value) =>
                                                    value
                                                        ? Promise.resolve()
                                                        : Promise.reject(
                                                              new Error(
                                                                  'Please acknowledge and agree to our Privacy & Terms Policy.'
                                                              )
                                                          ),
                                            },
                                        ]}
                                        initialValue={false}
                                    >
                                        <Checkbox>
                                            Please check to acknowledge our{' '}
                                            <a
                                                className="underline"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                href="https://parts-badger.com/terms-and-conditions/"
                                            >
                                                Privacy & Terms Policy
                                            </a>
                                        </Checkbox>
                                    </Form.Item>

                                    {contact?.is_hobbyist && (
                                        <Form.Item
                                            style={{
                                                marginBottom: 16,
                                            }}
                                            valuePropName="checked"
                                            name="acknowledge-order-authority"
                                            rules={[
                                                {
                                                    validator: (_, value) =>
                                                        value
                                                            ? Promise.resolve()
                                                            : Promise.reject(
                                                                  new Error(
                                                                      'Please acknowledge that this sales order is the authoritative document for your order.'
                                                                  )
                                                              ),
                                                },
                                            ]}
                                        >
                                            <Checkbox>
                                                I acknowledge that information in this sales order shall be the
                                                authority for the order, superseding any conflicting information
                                                provided in email, design files, note files for this order, or via any
                                                other method of communication.
                                            </Checkbox>
                                        </Form.Item>
                                    )}

                                    <Form.Item
                                        style={{
                                            marginBottom: 30,
                                        }}
                                        valuePropName="checked"
                                        name="acknowledge-tolerance-terms"
                                        rules={[
                                            {
                                                validator: (_, value) =>
                                                    value
                                                        ? Promise.resolve()
                                                        : Promise.reject(
                                                              new Error(
                                                                  'Please acknowledge the dimensional tolerance terms to proceed with the order.'
                                                              )
                                                          ),
                                            },
                                        ]}
                                    >
                                        <Checkbox>
                                            I understand that dimensional tolerances are quoted to print unless
                                            otherwise noted on the quote. If no print is provided, a tolerance table is
                                            missing, or if significant digits are not the same, then all dimensions will
                                            be held to one of the tables on the{' '}
                                            <a
                                                className="underline"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                                href="https://parts-badger.com/terms-and-conditions/"
                                            >
                                                Terms & Conditions
                                            </a>
                                        </Checkbox>
                                    </Form.Item>
                                </div>

                                <div className="text-center mt-2">
                                    <Button
                                        id="place-order-btn"
                                        loading={isSendingOrder}
                                        size="large"
                                        type="primary"
                                        htmlType="submit"
                                        block={true}
                                    >
                                        <span className="mr-1">Place Order</span>
                                        <Price value={order?.grand_total ?? 0} />
                                    </Button>
                                </div>
                            </div>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </>
    );
});
export default SalesOrderCreateForm;
